import { useEffect, useRef, useState } from 'react'
import { ArrowLeftRight } from 'lucide-react'

interface BeforeAfterSliderProps {
  before: string
  after: string
  title: string
  beforeLabel?: string
  afterLabel?: string
  hideCaption?: boolean
}

export function BeforeAfterSlider({
  before,
  after,
  title,
  beforeLabel = 'Prima',
  afterLabel = 'Dopo',
  hideCaption = false,
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const updatePosition = (clientX: number) => {
    const container = containerRef.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
    setPosition((x / rect.width) * 100)
  }

  const handlePointerDown = (event: React.PointerEvent) => {
    setIsDragging(true)
    updatePosition(event.clientX)
    ;(event.target as Element).setPointerCapture?.(event.pointerId)
  }

  useEffect(() => {
    if (!isDragging) return

    const handlePointerMove = (event: PointerEvent) => {
      updatePosition(event.clientX)
    }

    const handlePointerUp = () => {
      setIsDragging(false)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [isDragging])

  return (
    <figure className="w-full">
      <div
        ref={containerRef}
        onPointerDown={handlePointerDown}
        className="relative aspect-[4/3] overflow-hidden rounded-xl bg-heading cursor-ew-resize touch-none select-none"
        aria-label={`${beforeLabel} / ${afterLabel}: ${title}`}
      >
        {/* Immagine "dopo" come sfondo */}
        <img
          src={after}
          alt={`${title} — ${afterLabel}`}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />

        {/* Immagine "prima" con clip-path */}
        <img
          src={before}
          alt={`${title} — ${beforeLabel}`}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
          draggable={false}
        />

        {/* Linea divisoria */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_10px_rgba(0,0,0,0.5)]"
          style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
        />

        {/* Maniglia */}
        <div
          className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg text-heading"
          style={{ left: `${position}%`, transform: `translate(-50%, -50%)` }}
        >
          <ArrowLeftRight className="h-5 w-5" />
        </div>

        {/* Badge */}
        <span className="absolute top-4 left-4 bg-black/60 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded">
          {beforeLabel}
        </span>
        <span className="absolute top-4 right-4 bg-brand text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded">
          {afterLabel}
        </span>
      </div>

      {!hideCaption && (
        <figcaption className="mt-4 text-center">
          <h3 className="font-display text-xl text-heading">{title}</h3>
        </figcaption>
      )}
    </figure>
  )
}
